/**
 * Shared salary range helpers for JD create/edit, Job Details, and PDF.
 * Single text field (e.g. "3-5", "₹3 LPA – ₹5 LPA", or free text).
 */

const NOT_SPECIFIED = "Not specified";

const toNumber = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Extract numeric salary bounds from a stored salary_range string.
 * Returns { min, max, invalid, freeText }.
 */
export function parseSalaryRange(value) {
  if (value == null || value === "") {
    return { min: null, max: null, invalid: false, freeText: false };
  }

  let text = String(value).trim();
  if (!text) {
    return { min: null, max: null, invalid: false, freeText: false };
  }

  // Strip commas that appear BETWEEN digits (Indian notation, e.g.
  // "10,00,000" → "1000000"). Without this the regex below sees three
  // separate numbers [10, 0, 0] and rejects the whole thing as invalid.
  text = text.replace(/(\d),(?=\d)/g, "$1");

  // Capture numbers; only treat '-' as a sign when not between digits
  // (so "3-5" → [3, 5], while "-3" → [-3]).
  const matches = text.match(/(?<![\d.])-?\d+(?:\.\d+)?/g);
  if (!matches || matches.length === 0) {
    // Non-numeric free text (e.g. "Negotiable", "Competitive") — allowed as-is.
    return { min: null, max: null, invalid: false, freeText: true };
  }

  let nums = matches.map((m) => Number(m)).filter((n) => Number.isFinite(n));
  if (nums.some((n) => n < 0)) {
    return { min: null, max: null, invalid: true, freeText: false };
  }

  // Numbers >= 1,00,000 (1 lakh) are almost certainly raw INR amounts —
  // typically produced by the AI as "₹10,00,000" or by recruiters typing
  // full annual salary. LPA values in India are 0.5–500 in practice, so
  // any value in the lakhs is normalised to LPA (÷ 100000).
  nums = nums.map((n) => (n >= 100000 ? n / 100000 : n));

  if (nums.length === 1) {
    return { min: nums[0], max: null, invalid: false, freeText: false };
  }

  const min = nums[0];
  const max = nums[1];
  if (max < min) {
    return { min: null, max: null, invalid: true, freeText: false };
  }

  return { min, max, invalid: false, freeText: false };
}

/**
 * Build the canonical stored/display string, e.g. "₹3 LPA – ₹5 LPA".
 */
export function formatSalaryRange(minSalary, maxSalary) {
  const min = toNumber(minSalary);
  const max = toNumber(maxSalary);

  if (min == null && max == null) return "";
  if (min != null && min < 0) return "";
  if (max != null && max < 0) return "";

  if (min != null && max != null) {
    if (max < min) return "";
    return `₹${min} LPA – ₹${max} LPA`;
  }
  if (min != null) return `₹${min} LPA`;
  return `₹${max} LPA`;
}

/**
 * Safe display for Job Details / PDF. Never shows negatives or bad ranges.
 */
export function formatSalaryDisplay(value) {
  if (value == null || String(value).trim() === "") {
    return NOT_SPECIFIED;
  }

  const text = String(value).trim();
  const parsed = parseSalaryRange(text);
  if (parsed.invalid) {
    return NOT_SPECIFIED;
  }
  if (parsed.freeText) {
    return text;
  }

  const formatted = formatSalaryRange(parsed.min, parsed.max);
  return formatted || NOT_SPECIFIED;
}

/**
 * Validate a single salaryRange text field.
 * Returns { salaryRange?: message }.
 */
export function validateSalaryRangeInput(salaryRange) {
  const errors = {};
  if (salaryRange == null || String(salaryRange).trim() === "") {
    return errors;
  }

  // Same normalisation as parseSalaryRange — strip commas between digits so
  // Indian-format numbers ("10,00,000") aren't misread as invalid.
  const text = String(salaryRange).trim().replace(/(\d),(?=\d)/g, "$1");
  const matches = text.match(/(?<![\d.])-?\d+(?:\.\d+)?/g) || [];
  const nums = matches.map((m) => Number(m)).filter((n) => Number.isFinite(n));

  if (nums.some((n) => n < 0)) {
    errors.salaryRange = "Salary cannot be negative.";
    return errors;
  }

  if (nums.length >= 2 && nums[1] < nums[0]) {
    errors.salaryRange = "Maximum salary must be greater than or equal to minimum salary.";
    return errors;
  }

  return errors;
}

/** @deprecated Prefer validateSalaryRangeInput for the single-field UI. */
export function validateSalaryFields(minSalary, maxSalary) {
  const errors = {};
  const min = toNumber(minSalary);
  const max = toNumber(maxSalary);
  const minProvided = minSalary !== "" && minSalary != null;
  const maxProvided = maxSalary !== "" && maxSalary != null;

  if (minProvided && (min == null || min < 0)) {
    errors.minSalary = "Salary cannot be negative.";
  }
  if (maxProvided && (max == null || max < 0)) {
    errors.maxSalary = "Salary cannot be negative.";
  }
  if (!errors.minSalary && !errors.maxSalary && min != null && max != null && max < min) {
    errors.maxSalary = "Maximum salary must be greater than or equal to minimum salary.";
  }
  return errors;
}

/**
 * Normalize a salary text value for save: format numeric ranges, keep free text, clear invalid.
 */
export function normalizeSalaryForSave(salaryRange) {
  if (salaryRange == null || String(salaryRange).trim() === "") {
    return null;
  }
  const text = String(salaryRange).trim();
  const parsed = parseSalaryRange(text);
  if (parsed.invalid) {
    return null;
  }
  if (parsed.freeText) {
    return text;
  }
  return formatSalaryRange(parsed.min, parsed.max) || text;
}

export { NOT_SPECIFIED as SALARY_NOT_SPECIFIED };
